import { useState, FormEvent, useMemo } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { resources } from "@constants/hardcoded-strings";
import { ButtonTypes } from "@enums/button-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { SelectOption } from "@idw-types/common";
import { FormattedRpServiceConfigurationServiceOffering } from "@idw-types/data/rp-service-configuration";
import { sortStrings } from "@utils/string-util";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import { SelectFormGroup } from "../../common/select-form-group/select-form-group";

type AddRpServiceConfigurationServiceOfferingModalProps = {
    onModalClose: () => void;
    maxOrder: number;
    serviceOfferingRefreshRateDefault: string;
    rpServiceConfigurationServiceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>;
    addRpServiceConfigurationServiceOffering: (rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering) => void;
};

type AddRpServiceConfigurationServiceOfferingModalState = {
    rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering;
    selectedServiceOfferingOption: SelectOption;
    dataSourceSelectOptions: Array<SelectOption>;
    selectedDataSourceOption: SelectOption;
};

export const AddRpServiceConfigurationServiceOfferingModal = (props: AddRpServiceConfigurationServiceOfferingModalProps) => {
    const {
        onModalClose,
        serviceOfferingRefreshRateDefault,
        maxOrder,
        rpServiceConfigurationServiceOfferings,
        addRpServiceConfigurationServiceOffering
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.SMALL);
    const [state, setState] = useState<AddRpServiceConfigurationServiceOfferingModalState>({
        rpServiceConfigurationServiceOffering: null,
        selectedServiceOfferingOption: null,
        dataSourceSelectOptions: [],
        selectedDataSourceOption: null
    });

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        addRpServiceConfigurationServiceOffering({
            ...state.rpServiceConfigurationServiceOffering,
            status: EntityStatusTypes.ACTIVE,
            order: maxOrder,
            addedToService: true
        });
        onModalClose();
    };

    const serviceOfferingOptions = useMemo(() => {
        return rpServiceConfigurationServiceOfferings
            .map(so => ({ label: so.name, value: so.serviceOfferingID }))
            .sort((a, b) => sortStrings(a.label, b.label));
    }, [rpServiceConfigurationServiceOfferings]);

    const onChangeServiceOfferingSelect = (newValue: SelectOption) => {
        const selectedServiceOffering = rpServiceConfigurationServiceOfferings.find(so => so.serviceOfferingID === newValue.value);
        const dataSourceSelectOptions = selectedServiceOffering.dataSources.map(ds => ({ label: ds.value, value: ds.value }));
        const foundOption = selectedServiceOffering.dataSources.find(ds => isStringTrue(ds.defaultFlag));
        let selectedDataSourceOption = null;
        if (foundOption) {
            selectedDataSourceOption = { label: foundOption.value, value: foundOption.value };
            selectedServiceOffering.dataSource = foundOption.value;
            selectedServiceOffering.refreshRate = serviceOfferingRefreshRateDefault;
            selectedServiceOffering.weight = "1";
        }
        setState({
            ...state,
            rpServiceConfigurationServiceOffering: selectedServiceOffering,
            selectedServiceOfferingOption: newValue,
            dataSourceSelectOptions: dataSourceSelectOptions,
            selectedDataSourceOption: selectedDataSourceOption
        });
    };

    const onChangeServiceOfferingDataSourceSelect = (newValue: SelectOption) => {
        setState({
            ...state,
            selectedDataSourceOption: newValue,
            rpServiceConfigurationServiceOffering: {
                ...state.rpServiceConfigurationServiceOffering,
                dataSource: newValue.value
            }
        });
    };

    return (
        <DefaultModal
            automationId="add-service-offering-modal"
            ariaLabelledBy="add-service-offering-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-service-offering-title"} closeButton>
                    <Modal.Title>{resources.modals.addRpServiceConfigurationServiceOffering.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <SelectFormGroup
                        controlId={"service-offering-select"}
                        automationId={"service-offering-select"}
                        ariaLabel={resources.modals.addRpServiceConfigurationServiceOffering.inputs.service.label}
                        label={resources.modals.addRpServiceConfigurationServiceOffering.inputs.service.label}
                        disabled={false}
                        isClearable={false}
                        isRequired={true}
                        isSearchable={true}
                        labelWidth={labelWidth}
                        onChange={onChangeServiceOfferingSelect}
                        options={serviceOfferingOptions}
                        selectedOption={state.selectedServiceOfferingOption}
                    />
                    {state.selectedServiceOfferingOption && state.dataSourceSelectOptions.length > 1 && (
                        <SelectFormGroup
                            controlId={"service-offering-data-source-select"}
                            automationId={"service-offering-data-source-select"}
                            ariaLabel={resources.modals.addRpServiceConfigurationServiceOffering.inputs.dataSource.label}
                            label={resources.modals.addRpServiceConfigurationServiceOffering.inputs.dataSource.label}
                            disabled={false}
                            isClearable={false}
                            isRequired={true}
                            isSearchable={true}
                            labelWidth={labelWidth}
                            onChange={onChangeServiceOfferingDataSourceSelect}
                            options={state.dataSourceSelectOptions}
                            selectedOption={state.selectedDataSourceOption}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button id="add-service-offering-cancel-button" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <Button
                        id="add-service-offering-submit-button"
                        disabled={state.selectedServiceOfferingOption === null}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                    >
                        {resources.modals.addRpServiceConfigurationServiceOffering.buttons.addService.label}
                    </Button>
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};